import React, { useEffect, useState } from "react";
import { Col, Row, message, Card, DatePicker, Button, Typography, Spin } from "antd";
import {
    DollarOutlined,
    PercentageOutlined,
    TeamOutlined,
    WalletOutlined,
    FileProtectOutlined,
} from "@ant-design/icons";
import { useNavigate } from 'react-router-dom';
import { storage } from "../utils/storage";
import { useGetFinance } from "../services/finance.service";
import { IFinanceData } from "../types/finance";
import { Dayjs } from 'dayjs';

const { Text, Title } = Typography;

const PAGE_BG = "#f5f6fa";
const CARD_BG = "#ffffff";
const CARD_BORDER = "#e8eaf0";
const TEXT_PRIMARY = "#1e293b";
const TEXT_MUTED = "#64748b";

const formatCurrency = (value?: string | number) => `$${Number(value ?? 0).toFixed(2)}`;

const financeCards = (data: IFinanceData | null) => [
    {
        key: "overall",
        title: "Overall Platform Earnings",
        value: formatCurrency(data?.overallPlatformEarnings),
        icon: <DollarOutlined />,
        color: "#f59e0b",
        gradient: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)",
    },
    {
        key: "commission",
        title: "Platform Commission Earned",
        value: formatCurrency(data?.platformCommissionEarned),
        icon: <PercentageOutlined />,
        color: "#6366f1",
        gradient: "linear-gradient(135deg, #6366f1 0%, #818cf8 100%)",
    },
    {
        key: "attendantEarnings",
        title: "Total Attendant Earnings",
        value: formatCurrency(data?.totalAttendantEarnings),
        icon: <TeamOutlined />,
        color: "#22d3ee",
        gradient: "linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)",
    },
    {
        key: "fees",
        title: "Platform Fees",
        value: formatCurrency(data?.platformTotal),
        icon: <WalletOutlined />,
        color: "#10b981",
        gradient: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
    },
    {
        key: "taxes",
        title: "Taxes",
        value: formatCurrency(data?.taxTotal),
        icon: <FileProtectOutlined />,
        color: "#a78bfa",
        gradient: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)",
    },
];

const Finance: React.FC = () => {
    const navigate = useNavigate();
    const [financeData, setFinanceData] = useState<IFinanceData | null>(null);
    const { RangePicker } = DatePicker;
    const [selectedDates, setSelectedDates] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [loading, setLoading] = useState(true);
    const countryId = storage.getCountryId() ?? "";

    useEffect(() => {
        let token = storage.getToken() ?? "";
        if (!token) {
            navigate("/");
            return;
        }
        fetchFinance("", "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const fetchFinance = async (startDate: string, endDate: string) => {
        getFinanceMutation.mutate({ startDate: startDate, endDate: endDate, countryID: countryId });
    };

    const getFinanceMutation = useGetFinance({
        onSuccess: (response: any) => {
            if (response && response.data) {
                setFinanceData(response.data[0]);
                setLoading(false);
            } else {
                message.error("Unexpected response format");
                setLoading(false);
            }
        },
        onError: (error: any) => {
            message.error(error.message);
            setLoading(false);
        },
    });

    const handleSubmit = async () => {
        setLoading(true);
        const payload = {
            startDate: selectedDates[0]!.format("YYYY-MM-DD"),
            endDate: selectedDates[1]!.format("YYYY-MM-DD"),
        };
        fetchFinance(payload.startDate, payload.endDate);
    }

    return (
        <div style={{ background: PAGE_BG, minHeight: "100%", padding: "0 4px", color: TEXT_PRIMARY }}>
            <div className="flex justify-between align-middle pb-5">
                <Title level={3} style={{ color: TEXT_PRIMARY, margin: 0 }}>Finance</Title>
            </div>

            <Card
                style={{
                    marginBottom: 24,
                    borderRadius: 16,
                    border: `1px solid ${CARD_BORDER}`,
                    background: CARD_BG,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                }}
                bodyStyle={{ padding: "14px 20px" }}
            >
                <div className="flex items-center" style={{ flexWrap: "wrap", gap: 16 }}>
                    <div className="flex items-center gap-2">
                        <Text style={{ color: TEXT_PRIMARY, fontSize: 16, fontWeight: 600 }}>Filter</Text>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" style={{ color: TEXT_MUTED }} viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 01.8 1.6l-4.9 6.5V16a1 1 0 01-1.4.9l-2-1A1 1 0 018 15V10.1L3.2 4.6A1 1 0 013 4z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <RangePicker
                        className="border-none shadow-none focus:ring-0 focus:outline-none"
                        renderExtraFooter={() => 'extra footer'}
                        onChange={(dates: any) => {
                            setSelectedDates(dates);
                        }}
                    />
                    <Button
                        type="primary"
                        style={{ background: TEXT_PRIMARY, borderColor: TEXT_PRIMARY }}
                        onClick={handleSubmit}
                    >
                        Apply
                    </Button>
                </div>
            </Card>

            <Spin spinning={loading} size="large">
                <Row gutter={[20, 20]}>
                    {financeCards(financeData).map((kpi) => (
                        <Col xs={24} sm={12} xl={8} key={kpi.key}>
                            <Card
                                style={{
                                    background: CARD_BG,
                                    border: `1px solid ${CARD_BORDER}`,
                                    borderRadius: 16,
                                    overflow: "hidden",
                                    position: "relative",
                                    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.07)",
                                }}
                                bodyStyle={{ padding: "20px 24px" }}
                            >
                                <div
                                    style={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: 4,
                                        background: kpi.gradient,
                                        borderRadius: "4px 0 0 4px",
                                    }}
                                />
                                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                                    <div>
                                        <Text style={{ color: TEXT_MUTED, fontSize: 14, textTransform: "uppercase", letterSpacing: 1 }}>
                                            {kpi.title}
                                        </Text>
                                        <div style={{ marginTop: 6 }}>
                                            <Title level={3} style={{ color: TEXT_PRIMARY, margin: 0, fontWeight: 700 }}>
                                                {kpi.value}
                                            </Title>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 12,
                                            background: `${kpi.color}20`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 22,
                                            color: kpi.color,
                                            flex: "none",
                                        }}
                                    >
                                        {kpi.icon}
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Spin>
        </div>
    );
};

export default Finance;
